import * as React from 'react';
import { Check, Eye, FileText, Shield, Users } from 'lucide-react';

import { classNames } from '@/utils/classNames.utils';
import { MIN_LEN } from '@/constants';
import { useCreateSixReviewStep } from '@/hooks/createSix/useCreateSixReviewStep';
import { getCustomerInitials } from '@/utils/user.utils';

type ReviewPersonRole = 'Owner' | 'Member';

interface ReviewPersonRowModel {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: ReviewPersonRole;
}

interface ReviewPeopleGroupModel {
  title: string;
  people: ReviewPersonRowModel[];
}

export interface CreateSixReviewStepProps {
  onClose: () => void;
  className?: string;
}

function renderRoleBadge(role: ReviewPersonRole): React.JSX.Element {
  const roleIcon = role === 'Owner' ? <Shield className="h-3 w-3" /> : <Eye className="h-3 w-3" />;

  return (
    <div className="rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 flex items-center space-x-1">
      {roleIcon}
      <span>{role}</span>
    </div>
  );
}

function countPeople(groups: ReviewPeopleGroupModel[]): number {
  return groups.reduce((accumulator, group) => accumulator + group.people.length, MIN_LEN);
}

function CreateSixReviewStep(props: CreateSixReviewStepProps): React.JSX.Element {
  void props;

  const { state } = useCreateSixReviewStep();
  const { draft, internalUsers, externalUsers } = state;

  const internalUsersById = React.useMemo(() => {
    const mapping = new Map<string, (typeof internalUsers)[number]>();
    internalUsers.forEach((user) => {
      mapping.set(user.id, user);
    });
    return mapping;
  }, [internalUsers]);

  const externalUsersById = React.useMemo(() => {
    const mapping = new Map<string, (typeof externalUsers)[number]>();
    externalUsers.forEach((user) => {
      mapping.set(user.id, user);
    });
    return mapping;
  }, [externalUsers]);

  const groups = React.useMemo<ReviewPeopleGroupModel[]>(() => {
    const ownerRows: ReviewPersonRowModel[] = draft.ownerIds
      .map((ownerId) => internalUsersById.get(ownerId))
      .filter((user): user is NonNullable<typeof user> => user !== undefined)
      .map((user) => ({
        id: `owner-${user.id}`,
        name: user.name,
        email: user.email,
        initials: getCustomerInitials(user.name),
        role: 'Owner',
      }));

    const ownerIdsSet = new Set(draft.ownerIds);

    const memberRows: ReviewPersonRowModel[] = draft.internalParticipantIds
      .filter((participantId) => !ownerIdsSet.has(participantId))
      .map((participantId) => internalUsersById.get(participantId))
      .filter((user): user is NonNullable<typeof user> => user !== undefined)
      .map((user) => ({
        id: `member-${user.id}`,
        name: user.name,
        email: user.email,
        initials: getCustomerInitials(user.name),
        role: 'Member',
      }));

    const externalContactRows: ReviewPersonRowModel[] = draft.externalParticipantIds
      .map((externalId) => externalUsersById.get(externalId))
      .filter((user): user is NonNullable<typeof user> => user !== undefined)
      .map((user) => ({
        id: `external-${user.id}`,
        name: user.name,
        email: user.email,
        initials: getCustomerInitials(user.name),
        role: 'Member',
      }));

    const newExternalRows: ReviewPersonRowModel[] = draft.newExternalUsers.map((user, index) => ({
      id: `new-external-${String(index)}-${user.email}`,
      name: user.name,
      email: user.email,
      initials: getCustomerInitials(user.name),
      role: 'Member',
    }));

    const internalGroupPeople = [...ownerRows, ...memberRows];
    const externalGroupPeople = [...externalContactRows, ...newExternalRows];

    const nextGroups: ReviewPeopleGroupModel[] = [];

    if (internalGroupPeople.length > MIN_LEN) {
      nextGroups.push({ title: 'Internal Staff', people: internalGroupPeople });
    }

    if (externalGroupPeople.length > MIN_LEN) {
      nextGroups.push({ title: 'External People', people: externalGroupPeople });
    }

    return nextGroups;
  }, [
    draft.externalParticipantIds,
    draft.internalParticipantIds,
    draft.newExternalUsers,
    draft.ownerIds,
    externalUsersById,
    internalUsersById,
  ]);

  const totalPeopleCount = countPeople(groups);

  const sixName = draft.name;
  const sixDescription = draft.description;

  return (
    <div className={classNames('space-y-6', props.className)}>
      <div>
        <h3 className="text-lg font-semibold mb-2">Review &amp; Confirm</h3>
        <p className="text-muted-foreground">Review your SIX configuration before creating it.</p>
      </div>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="flex flex-col space-y-1.5 p-6 pb-3">
          <h3 className="font-semibold tracking-tight flex items-center space-x-2 text-base">
            <FileText className="h-5 w-5" />
            <span>SIX Information</span>
          </h3>
        </div>

        <div className="p-6 pt-0 space-y-3">
          <div>
            <div className="font-medium">{sixName}</div>
            {sixDescription.trim().length > MIN_LEN ? (
              <div className="text-sm text-muted-foreground">{sixDescription}</div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="flex flex-col space-y-1.5 p-6 pb-3">
          <h3 className="font-semibold tracking-tight flex items-center space-x-2 text-base">
            <Users className="h-5 w-5" />
            <span>
              People ({totalPeopleCount})
            </span>
          </h3>
        </div>

        <div className="p-6 pt-0 space-y-6">
          {groups.map((group) => (
            <div key={group.title} className="space-y-3">
              <div className="flex items-center space-x-2 text-sm font-medium text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>
                  {group.title} ({group.people.length})
                </span>
              </div>

              <div className="space-y-2">
                {group.people.map((person) => (
                  <div key={person.id} className="flex items-center justify-between py-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">{person.initials}</span>
                      </div>

                      <div>
                        <div className="font-medium text-sm">{person.name}</div>
                        <div className="text-xs text-muted-foreground">{person.email}</div>
                      </div>
                    </div>

                    {renderRoleBadge(person.role)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border text-card-foreground shadow-sm border-primary/20 bg-primary/5">
        <div className="p-4">
          <div className="flex items-start space-x-3">
            <Check className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <div className="font-medium text-primary">Ready to Create</div>
              <div className="text-sm text-muted-foreground mt-1">
                Your SIX "{sixName}" is configured and ready to be created.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

CreateSixReviewStep.displayName = 'CreateSixReviewStep';

export default CreateSixReviewStep;
